import {
  buildExamPublicLandingModel,
  buildExamSelectionPageModel,
  buildUserLearningContext,
} from '@examflow/learning-space';
import {
  HOME_SEO_DESCRIPTION,
  HOME_SEO_TITLE,
  SITE_ORIGIN,
  type RouteSeoDescriptor,
} from './site';

const EXAM_PATH_RE = /^\/exams\/([a-z0-9_-]+)\/?$/i;

function homeSeo(): RouteSeoDescriptor {
  const visitor = buildExamSelectionPageModel(buildUserLearningContext(null, null));
  return {
    title: HOME_SEO_TITLE,
    description: visitor.hero.description || HOME_SEO_DESCRIPTION,
    canonicalPath: '/',
    robots: 'index, follow',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'ExamFlow',
      alternateName: '考流',
      url: SITE_ORIGIN,
      description: visitor.hero.description || HOME_SEO_DESCRIPTION,
    },
  };
}

function examSeo(examId: string): RouteSeoDescriptor | null {
  const model = buildExamPublicLandingModel(examId);
  if (!model) return null;
  const canonicalPath = `/exams/${model.examId}`;
  return {
    title: `${model.examName} 考试空间 · ExamFlow`,
    description: model.description,
    canonicalPath,
    robots: 'index, follow',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${model.examName} 考试空间`,
      description: model.description,
      url: `${SITE_ORIGIN}${canonicalPath}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'ExamFlow',
        url: SITE_ORIGIN,
      },
      about: {
        '@type': 'Thing',
        name: model.examName,
        description: model.description,
      },
    },
  };
}

function noindexSeo(pathname: string): RouteSeoDescriptor {
  return {
    title: HOME_SEO_TITLE,
    description: HOME_SEO_DESCRIPTION,
    canonicalPath: pathname.startsWith('/') ? pathname : `/${pathname}`,
    robots: 'noindex, nofollow',
    ogType: 'website',
  };
}

/**
 * Resolve document head for a SPA pathname (no search/hash).
 * Public indexable: `/` and `/exams/:examId` for available exams only.
 */
export function resolveRouteSeo(pathname: string): RouteSeoDescriptor {
  const path = pathname.split('?')[0]?.split('#')[0] || '/';
  const normalized = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  if (normalized === '/' || normalized === '') {
    return homeSeo();
  }

  const examMatch = EXAM_PATH_RE.exec(normalized);
  if (examMatch?.[1]) {
    const seo = examSeo(examMatch[1].toLowerCase());
    if (seo) return seo;
    return noindexSeo(normalized);
  }

  return noindexSeo(normalized);
}
